/* eslint-disable react/no-unused-prop-types */
import React, { useCallback, useMemo } from "react";
import { bool, func, number, shape, string } from "prop-types";
import { connect } from "react-redux";

import { TransferExecuteInternal } from "@ui-kit/components/index.es";

import { selectors as formSelectors } from "reducers/form";
import { selectors as i18nSelectors } from "reducers/i18n";
import { selectors as sessionSelectors } from "reducers/session";
import { selectors as settingsSelectors } from "reducers/settings";
import { selectors as templateSelectors } from "reducers/template";
import { selectors as transferSelectors } from "reducers/transfer";

import Message from "pages/v2/transfer/scheduler/Message";
import useTransferInternalExecuteLogic from "v3/pages/transfers/logic/useTransferInternalExecuteLogic";
import { createTransferExecuteFormLabels } from "v3/pages/transfers/transferExecuteLabels";
import {
    TransferExecuteModals,
    useTransferExecuteFormikSync,
    useTransferExecuteLimitsRenderer,
    resolveTransferExecuteCurrency,
} from "v3/pages/transfers/transferExecuteBridgeShared";

function TransferExecuteInternalBridge(props) {
    const logic = useTransferInternalExecuteLogic(props);
    const getLabels = useMemo(() => createTransferExecuteFormLabels("internal"), []);
    const renderLimits = useTransferExecuteLimitsRenderer(logic);

    const renderScheduleSummary = useCallback(() => {
        if (logic.values?.scheduler?.selectedOption === undefined) {
            return null;
        }
        return <Message value={logic.values.scheduler || { valueDate: "" }} />;
    }, [logic.values?.scheduler]);

    const formikFields = useMemo(
        () => ({
            debitAccount: logic.debitAccount,
            creditAccount: logic.creditAccount,
            amount: logic.amount,
            reference: logic.reference,
            sendEmail: logic.sendEmail,
            email: logic.email,
            emailText: logic.emailText,
        }),
        [
            logic.debitAccount,
            logic.creditAccount,
            logic.amount,
            logic.reference,
            logic.sendEmail,
            logic.email,
            logic.emailText,
        ],
    );

    useTransferExecuteFormikSync(logic.formikProps, formikFields);

    const handleSaveDraft = useCallback(
        (values) => {
            logic.handleSaveDraftClick({ values });
        },
        [logic],
    );

    const handleSaveTemplate = useCallback(
        (values) => {
            logic.handleCreateTemplate({ values });
        },
        [logic],
    );

    const selected = typeof logic.selectedAccount === "function" ? logic.selectedAccount() : logic.selectedAccount;

    return (
        <>
            <TransferExecuteInternal
                getLabels={getLabels}
                loading={logic.showInitialSpinner}
                loadingSubmit={logic.fetchingSubmit}
                loadingDraft={logic.fetchingDraft}
                initialValues={logic.initialValues}
                validationSchema={logic.validationSchema}
                onSubmit={logic.onSubmit}
                setFormikProps={logic.setFormikProps}
                debitAccountOptions={logic.mapAccounts}
                creditAccountOptions={logic.creditAccountOptions}
                onDebitAccountChange={logic.handleDebitAccountSelect}
                onCreditAccountChange={logic.handleCreditAccountSelect}
                hideDebitAmount={logic.enableHideAmounts && logic.hideAmount && logic.isMobile}
                hideCreditAmount={logic.enableHideAmounts && logic.hideAmount && logic.isMobile}
                currency={resolveTransferExecuteCurrency(logic.selectedCurrency)}
                referenceMaxLength={logic.referenceMaxLength}
                extendedAmountEnabled={logic.extendedAmountVariableEnabled}
                showTadWarning={logic.tadWarningShowed}
                tadHelperText={logic.tadWarningText}
                renderLimits={renderLimits}
                showScheduleButton={logic.agendaVisible}
                onScheduleClick={logic.openScheduleModal}
                renderScheduleSummary={renderScheduleSummary}
                onSaveDraft={handleSaveDraft}
                onSaveTemplate={handleSaveTemplate}
                isDraftInactive={logic.isDraftButtonInactive}
                isTemplateInactive={false}
                sendEmail={logic.sendEmail}
                onSendEmailToggle={logic.handleSendEmailToggle}
                onReferenceChange={logic.handleReferenceChange}
                onEmailChange={logic.handleEmailChange}
                onEmailTextChange={logic.handleEmailTextChange}
                onAmountBlur={logic.handleAmountBlur}
                onAmountKeyDown={logic.customHandleKeyDown}
                onTadInfoClick={logic.openTADModal}
            />
            <TransferExecuteModals
                logic={logic}
                idActivity={logic.idActivity}
                idForm={logic.idForm}
                valuesForTemplate={{ ...logic.values, currency: selected?.currency }}
            />
        </>
    );
}

TransferExecuteInternalBridge.propTypes = {
    dispatch: func.isRequired,
    isDesktop: bool.isRequired,
    onCreateTemplate: func.isRequired,
    agendaVisible: bool.isRequired,
    setAgendaVisible: func.isRequired,
    isTemplatesVisible: bool.isRequired,
    setIsTemplatesVisible: func.isRequired,
    location: shape({}).isRequired,
    fetching: bool.isRequired,
    fetchingDraft: bool.isRequired,
    fetchingSubmit: bool.isRequired,
    accounts: shape({}).isRequired,
    limits: shape({}).isRequired,
    limit: number.isRequired,
    limitUsed: number.isRequired,
    summary: shape({}).isRequired,
    templates: shape({}).isRequired,
    isCVU: bool.isRequired,
    hideAmount: bool.isRequired,
    lang: string.isRequired,
    idTransaction: string,
};

TransferExecuteInternalBridge.defaultProps = {
    idTransaction: "",
};

const mapStateToProps = (state) => ({
    fetching: transferSelectors.getFetching(state),
    fetchingDraft: formSelectors.getFetchingDraft(state),
    fetchingSubmit: transferSelectors.getFetchingSubmit(state),
    accounts: transferSelectors.getAccounts(state),
    externalOwnAccounts: transferSelectors.getExternalOwnAccounts(state),
    limit: transferSelectors.getLimit(state),
    limitUsed: transferSelectors.getLimitUsed(state),
    limits: transferSelectors.getLimits(state),
    summary: transferSelectors.getSummary(state),
    templates: templateSelectors.getTemplateList(state),
    isCVU: transferSelectors.getIsCVU(state),
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    lang: i18nSelectors.getLang(state),
    isRetailEnvironment: sessionSelectors.isRetailEnvironment(state),
    hideAmount: settingsSelectors.getUserData(state)?.hideBalance,
});

export default connect(mapStateToProps)(TransferExecuteInternalBridge);
